// Gathering detail page — pure info surface.
//
// The room, device check, countdown, and post-gathering recap all live on
// the `/gatherings/:id/live` route. From here you can read about the
// gathering, see who's coming, and RSVP. When it's close to start, the
// CTA bar offers a "Join Gathering" button that opens /live in a new tab.

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CalendarDays,
  Clock,
  Loader2,
  Lock,
  MapPin,
  Users,
  Video,
} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  ModuleBreadcrumbs,
  Skeleton,
  useGql,
} from '@ah/design';
import { GatheringsLayout } from './GatheringsLayout.tsx';
import {
  GATHERING_QUERY,
  GATHERING_DETAIL_RESERVE_MUTATION,
  GATHERING_DETAIL_CANCEL_MUTATION,
  type GatheringDetail as GatheringDetailType,
  type GatheringUser,
  type GatheringParticipantRow,
} from '../shared/index.ts';

// ── helpers ──────────────────────────────────────────────────────────

function userInitials(u: GatheringUser): string {
  if (u.firstName) return `${u.firstName[0]}${u.lastName?.[0] ?? ''}`.toUpperCase();
  return (u.name?.[0] ?? 'U').toUpperCase();
}
function displayName(u: GatheringUser): string {
  if (u.firstName) return `${u.firstName} ${u.lastName ?? ''}`.trim();
  return u.name ?? 'Unknown';
}
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit',
  });
}
function formatDuration(startIso: string, endIso: string): string {
  const mins = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

// ── participants panel ───────────────────────────────────────────────

function ParticipantsPanel({ participants }: { participants: GatheringParticipantRow[] | null | undefined }) {
  const list = participants ?? [];
  return (
    <Card className="h-fit">
      <CardHeader>
        <h2 className="font-semibold text-foreground flex items-center gap-2">
          <Users className="w-4 h-4" /> Participants ({list.length})
        </h2>
      </CardHeader>
      <CardContent className="space-y-1">
        {list.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 -mx-2">
            <Avatar className="w-9 h-9 shrink-0">
              <AvatarImage src={p.user.pictureUrl ?? ''} alt={displayName(p.user)} />
              <AvatarFallback className="text-xs">{userInitials(p.user)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{displayName(p.user)}</p>
              <p className="text-xs text-muted-foreground capitalize">{p.role}</p>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No participants yet</p>}
      </CardContent>
    </Card>
  );
}

// ── main page ────────────────────────────────────────────────────────

export function GatheringDetail() {
  const gql = useGql();
  const qc = useQueryClient();
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['gathering', id],
    queryFn: async () => gql.request<{ me: { id: string } | null; gathering: GatheringDetailType | null }>(GATHERING_QUERY, { id }),
    enabled: !!id,
    refetchOnMount: 'always',
  });
  const gathering = data?.gathering;

  const reserve = useMutation({
    mutationFn: () => gql.request(GATHERING_DETAIL_RESERVE_MUTATION, { gatheringId: id, email: null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gathering', id] }),
  });
  const cancel = useMutation({
    mutationFn: () => gql.request(GATHERING_DETAIL_CANCEL_MUTATION, { gatheringId: id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gathering', id] }),
  });

  if (isLoading) {
    return (
      <GatheringsLayout>
        <ModuleBreadcrumbs segments={[{ label: 'Gatherings', to: '/gatherings' }, { label: '...' }]} />
        <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
          <Skeleton className="h-8 w-48" /><Skeleton className="h-4 w-96" /><Skeleton className="h-64 w-full" />
        </div>
      </GatheringsLayout>
    );
  }

  if (error || !gathering) {
    return (
      <GatheringsLayout>
        <ModuleBreadcrumbs segments={[{ label: 'Gatherings', to: '/gatherings' }, { label: 'Not Found' }]} />
        <div className="max-w-5xl mx-auto px-6 py-8">
          <p className="text-sm text-destructive">{(error as Error)?.message ?? 'Gathering not found'}</p>
        </div>
      </GatheringsLayout>
    );
  }

  const status = gathering.status ?? 'scheduled';
  const isOnline = gathering.kind === 'roundtable';
  const isPrivate = gathering.visibility === 'circle' && gathering.circle?.name;
  const reserved = gathering.currentUserParticipant?.status === 'reserved';
  const ended = status === 'ended' || new Date(gathering.endsAt).getTime() < Date.now();
  const canJoin = reserved && isOnline && !ended;

  const breadcrumbBase = ended
    ? { label: 'Past', to: '/gatherings/past' }
    : { label: 'Upcoming', to: '/gatherings' };

  return (
    <GatheringsLayout>
      <ModuleBreadcrumbs segments={[{ label: 'Gatherings', to: '/gatherings' }, breadcrumbBase, { label: gathering.title }]} />
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Cover */}
        {gathering.coverUrl ? (
          <div className="aspect-[3/1] bg-muted overflow-hidden rounded-2xl">
            <img src={gathering.coverUrl} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="aspect-[3/1] relative overflow-hidden rounded-2xl bg-muted/60">
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/40">
              <CalendarDays size={64} strokeWidth={1.25} />
            </div>
          </div>
        )}

        {/* Header */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="text-xs">{gathering.kind}</Badge>
            {status === 'live' && (
              <Badge variant="default" className="text-xs">
                <span className="mr-1.5 w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse" />
                Live
              </Badge>
            )}
            {ended && <Badge variant="outline" className="text-xs">Ended</Badge>}
          </div>
          <h1 className="text-3xl font-bold text-foreground">{gathering.title}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {formatDateTime(gathering.startsAt)}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {formatDuration(gathering.startsAt, gathering.endsAt)}</span>
            {isOnline ? (
              <span className="flex items-center gap-1.5"><Video className="w-3.5 h-3.5" /> Online</span>
            ) : gathering.location ? (
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {gathering.location}</span>
            ) : null}
          </div>
        </div>

        {/* Visibility banner */}
        {isPrivate && (
          <div className="rounded-2xl border border-border/60 bg-muted/30 px-4 py-3 flex items-center gap-2 text-sm">
            <Lock size={14} className="shrink-0 text-muted-foreground" />
            <span className="text-muted-foreground">
              Private — open to members of <span className="text-foreground font-medium">{gathering.circle!.name}</span>.
            </span>
          </div>
        )}

        {/* CTA bar */}
        {!ended && (
          <div className="flex flex-wrap items-center gap-3">
            {reserved ? (
              <Button variant="outline" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                {cancel.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Cancel RSVP
              </Button>
            ) : (
              <Button onClick={() => reserve.mutate()} disabled={reserve.isPending}>
                {reserve.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                RSVP
              </Button>
            )}
            {canJoin && (
              <Button
                size="lg"
                onClick={() => window.open(`/gatherings/${gathering.id}/live`, '_blank', 'noopener')}
              >
                <Video className="w-4 h-4 mr-2" /> Join Gathering
              </Button>
            )}
            {ended && (
              <Button variant="outline" onClick={() => navigate(`/gatherings/${gathering.id}/live`)}>
                View recap
              </Button>
            )}
          </div>
        )}

        {/* Description + participants */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 space-y-4">
            {gathering.description && (
              <Card>
                <CardHeader><h2 className="font-semibold text-foreground">About</h2></CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{gathering.description}</p>
                </CardContent>
              </Card>
            )}
            {ended && (
              <Card>
                <CardContent className="pt-6 text-center space-y-2">
                  <p className="font-semibold text-foreground">This gathering has ended</p>
                  <p className="text-sm text-muted-foreground">Open the recap for the summary and share options.</p>
                  <Button onClick={() => navigate(`/gatherings/${gathering.id}/live`)}>View recap</Button>
                </CardContent>
              </Card>
            )}
          </div>
          <div className="space-y-4">
            <ParticipantsPanel participants={gathering.participants} />
          </div>
        </div>
      </div>
    </GatheringsLayout>
  );
}

